import EmptyState from '@/components/EmptyState';
import {
  usePrinters,
  usePrintRouting,
  usePrintSettings,
  useUpdatePrintSettings,
  useUpsertRouting,
} from '@/features/print/hooks/usePrint';
import { useT } from '@/i18n';
import {
  Block,
  BlockFooter,
  BlockTitle,
  List,
  ListItem,
  Navbar,
  Page,
  Preloader,
  f7,
} from 'framework7-react';
import { ToggleSwitch } from '@/components/ToggleSwitch';
import { useMemo, useRef } from 'react';

const toast = (text: string) => f7.toast.show({ text, closeTimeout: 2000, position: 'bottom' });

const DEFAULT = 'DEFAULT';

// Kitchen-ticket routing. The whole round prints to the main (default) printer
// unless the operator turns on "split by station": each station is a page that
// claims a set of menu categories and prints them to its own printer. With
// splitting on, an optional expo copy reprints every item to the main printer
// for the pass. Category→printer routing was removed - stations (pages) are the
// only routing model now.
function PrintRoutingPage() {
  const t = useT();
  const { data: printers } = usePrinters();
  const { data: routing, isLoading: routingLoading } = usePrintRouting();
  const { data: settings } = usePrintSettings();
  const updateSettings = useUpdatePrintSettings();
  const upsert = useUpsertRouting();

  const hasPrinters = (printers?.length ?? 0) > 0;
  const split = settings?.splitKitchenTickets ?? false;
  const expo = settings?.printExpoTicket ?? false;
  const defaultPrinterId = routing?.defaultPrinterId ?? null;
  const pages = useMemo(() => routing?.pages ?? [], [routing]);

  const printerName = (id: string | null) =>
    (id && printers?.find((p) => p.id === id)?.name) || t.print.noneSet;

  const anchorRef = useRef<HTMLElement | null>(null);

  // Set the main printer through the routing upsert so pages + default save
  // atomically; the existing pages are passed through untouched.
  const saveDefault = (printerId: string | null) => {
    upsert.mutate({
      pages: pages.map((p) => ({
        name: p.name,
        categoryIds: p.categoryIds,
        productIds: p.productIds,
        printerId: p.printerId,
      })),
      defaultPrinterId: printerId,
    });
  };

  // Main-printer picker: the Settings actions-menu idiom, anchored to the row's
  // own value so the popover opens beside it on wide viewports.
  const openDefaultPicker = () => {
    const choices = [
      { value: DEFAULT, text: t.print.noneSet },
      ...(printers ?? []).map((p) => ({ value: p.id, text: p.name })),
    ];
    f7.actions
      .create({
        targetEl: anchorRef.current ?? undefined,
        buttons: [
          [
            { label: true, text: t.print.mainPrinterLabel },
            ...choices.map((c) => ({
              text: c.text,
              bold: c.value === (defaultPrinterId ?? DEFAULT),
              onClick: () => saveDefault(c.value === DEFAULT ? null : c.value),
            })),
            { text: t.cancel, color: 'red' },
          ],
        ],
      })
      .open();
  };

  const setSplit = (on: boolean) =>
    updateSettings.mutate(
      { splitKitchenTickets: on },
      { onSuccess: () => toast(t.print.splitUpdated) },
    );
  const setExpo = (on: boolean) =>
    updateSettings.mutate({ printExpoTicket: on }, { onSuccess: () => toast(t.print.expoUpdated) });

  const stations = useMemo(() => [...pages].sort((a, b) => a.name.localeCompare(b.name)), [pages]);

  return (
    <Page name="print-routing">
      <Navbar title={t.print.routingTitle} backLink={t.back} />

      {routingLoading && (
        <Block className="text-align-center">
          <Preloader />
        </Block>
      )}

      {!routingLoading && !hasPrinters && (
        <EmptyState
          icon="printer"
          title={t.print.noPrintersTitle}
          description={t.print.routingNoPrintersDesc}
        />
      )}

      {!routingLoading && hasPrinters && (
        <>
          {/* Main printer — base target for the whole round (split off) and the
              fallback for categories in no station (split on). */}
          <BlockTitle>{t.print.mainPrinterLabel}</BlockTitle>
          <BlockFooter style={{ marginTop: -6 }}>{t.print.mainPrinterFooter}</BlockFooter>
          <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
            <ListItem
              title={t.print.mainPrinterLabel}
              link="#"
              onClick={(e: React.MouseEvent) => {
                e.preventDefault();
                openDefaultPicker();
              }}
            >
              <span
                slot="after"
                ref={(el) => {
                  anchorRef.current = el;
                }}
                style={{ fontWeight: 600 }}
              >
                {printerName(defaultPrinterId)}
              </span>
            </ListItem>
          </List>

          {/* Split switch — the master control. */}
          <BlockTitle>{t.print.splitTitle}</BlockTitle>
          <BlockFooter style={{ marginTop: -6 }}>{t.print.splitFooter}</BlockFooter>
          <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
            <ListItem title={t.print.splitToggleLabel}>
              <ToggleSwitch slot="after" checked={split} onChange={setSplit} />
            </ListItem>
          </List>

          {split && (
            <>
              <BlockTitle>{t.print.stationsTitle}</BlockTitle>
              <BlockFooter style={{ marginTop: -6 }}>{t.print.stationsFooter}</BlockFooter>
              <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
                {stations.map((s) => (
                  <ListItem
                    key={s.id}
                    title={s.name}
                    after={printerName(s.printerId)}
                    text={t.print.stationSelectionSummary(
                      s.categoryIds.length,
                      s.productIds.length,
                    )}
                    link={`/settings/printers/routing/${s.id}`}
                  />
                ))}
                <ListItem title={t.print.addStationLabel} link="/settings/printers/routing/new">
                  <i slot="media" className="f7-icons" style={{ color: 'var(--f7-theme-color)' }}>
                    plus_circle_fill
                  </i>
                </ListItem>
              </List>

              {/* Expo copy — only meaningful while splitting is on. */}
              <BlockTitle>{t.print.expoTitle}</BlockTitle>
              <BlockFooter style={{ marginTop: -6 }}>{t.print.expoFooter}</BlockFooter>
              <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
                <ListItem title={t.print.expoToggleLabel}>
                  <ToggleSwitch slot="after" checked={expo} onChange={setExpo} />
                </ListItem>
              </List>
            </>
          )}
        </>
      )}
    </Page>
  );
}

export default PrintRoutingPage;
